/**
 * Route-to-permission mapping and auth guard utilities.
 *
 * Maps route path prefixes to required permission codes (OR logic —
 * user needs at least ONE of the listed permissions to access).
 * Used by layout routes for auth guards and by AppShell to filter sidebar items.
 *
 * Permission codes aligned with backend seed data:
 * see backend/platform-service/docs/module-permissions.md
 */

// ============================================
// Permission Codes
// ============================================

export const PERMISSIONS = {
  // Tenants
  TENANTS_VIEW: 'TENANTS_VIEW',
  TENANTS_CREATE: 'TENANTS_CREATE',
  TENANTS_UPDATE: 'TENANTS_UPDATE',
  TENANTS_ACTIVATE: 'TENANTS_ACTIVATE',
  TENANTS_DELETE: 'TENANTS_DELETE',

  // Legal Entities
  ENTITIES_VIEW: 'ENTITIES_VIEW',
  ENTITIES_CREATE: 'ENTITIES_CREATE',
  ENTITIES_UPDATE: 'ENTITIES_UPDATE',
  ENTITIES_DELETE: 'ENTITIES_DELETE',

  // Employees
  EMPLOYEES_VIEW: 'EMPLOYEES_VIEW',
  EMPLOYEES_CREATE: 'EMPLOYEES_CREATE',
  EMPLOYEES_UPDATE: 'EMPLOYEES_UPDATE',
  EMPLOYEES_DELETE: 'EMPLOYEES_DELETE',

  // Onboarding
  ONBOARDING_MANAGE: 'ONBOARDING_MANAGE',

  // Employment Types
  EMPLOYMENT_TYPES_VIEW: 'EMPLOYMENT_TYPES_VIEW',
  EMPLOYMENT_TYPES_CREATE: 'EMPLOYMENT_TYPES_CREATE',
  EMPLOYMENT_TYPES_UPDATE: 'EMPLOYMENT_TYPES_UPDATE',
  EMPLOYMENT_TYPES_DELETE: 'EMPLOYMENT_TYPES_DELETE',

  // Departments (V30 — tenant-scoped, visibility=TENANT)
  DEPARTMENTS_VIEW: 'DEPARTMENTS_VIEW',
  DEPARTMENTS_CREATE: 'DEPARTMENTS_CREATE',
  DEPARTMENTS_UPDATE: 'DEPARTMENTS_UPDATE',
  DEPARTMENTS_DELETE: 'DEPARTMENTS_DELETE',

  // Job Titles (V30 — tenant-scoped, visibility=TENANT)
  JOB_TITLES_VIEW: 'JOB_TITLES_VIEW',
  JOB_TITLES_CREATE: 'JOB_TITLES_CREATE',
  JOB_TITLES_UPDATE: 'JOB_TITLES_UPDATE',
  JOB_TITLES_DELETE: 'JOB_TITLES_DELETE',

  // Forms (admin-only — MODULE_FORMS, visibility=WORKPAY)
  FORMS_VIEW: 'FORMS_VIEW',
  FORMS_CREATE: 'FORMS_CREATE',
  FORMS_UPDATE: 'FORMS_UPDATE',
  FORMS_DELETE: 'FORMS_DELETE',

  // Forms Access (tenant/employee form fill — MODULE_FORMS_ACCESS, visibility=ALL, V27)
  // BE accepts these via OR with FORMS_VIEW/UPDATE on form-service controllers.
  // _UPDATE here means form-response save (not form-schema edit).
  FORMS_ACCESS_VIEW: 'FORMS_ACCESS_VIEW',
  FORMS_ACCESS_UPDATE: 'FORMS_ACCESS_UPDATE',

  // Tenant Templates
  TENANT_TEMPLATES_VIEW: 'TENANT_TEMPLATES_VIEW',
  TENANT_TEMPLATES_CREATE: 'TENANT_TEMPLATES_CREATE',
  TENANT_TEMPLATES_UPDATE: 'TENANT_TEMPLATES_UPDATE',
  TENANT_TEMPLATES_DELETE: 'TENANT_TEMPLATES_DELETE',

  // Entity Templates
  ENTITY_TEMPLATES_VIEW: 'ENTITY_TEMPLATES_VIEW',
  ENTITY_TEMPLATES_CREATE: 'ENTITY_TEMPLATES_CREATE',
  ENTITY_TEMPLATES_UPDATE: 'ENTITY_TEMPLATES_UPDATE',
  ENTITY_TEMPLATES_DELETE: 'ENTITY_TEMPLATES_DELETE',

  // Employee Templates (admin — MODULE_EMPLOYEE_TEMPLATES, visibility=WORKPAY)
  EMPLOYEE_TEMPLATES_VIEW: 'EMPLOYEE_TEMPLATES_VIEW',
  EMPLOYEE_TEMPLATES_CREATE: 'EMPLOYEE_TEMPLATES_CREATE',
  EMPLOYEE_TEMPLATES_UPDATE: 'EMPLOYEE_TEMPLATES_UPDATE',
  EMPLOYEE_TEMPLATES_DELETE: 'EMPLOYEE_TEMPLATES_DELETE',

  // Employee Templates Access (tenant/employee read during onboarding —
  // MODULE_EMPLOYEE_TEMPLATES_ACCESS, visibility=ALL, V27). BE accepts via OR with EMPLOYEE_TEMPLATES_VIEW.
  EMPLOYEE_TEMPLATES_ACCESS_VIEW: 'EMPLOYEE_TEMPLATES_ACCESS_VIEW',

  // Product Types
  PRODUCT_TYPES_VIEW: 'PRODUCT_TYPES_VIEW',
  PRODUCT_TYPES_CREATE: 'PRODUCT_TYPES_CREATE',
  PRODUCT_TYPES_UPDATE: 'PRODUCT_TYPES_UPDATE',
  PRODUCT_TYPES_DELETE: 'PRODUCT_TYPES_DELETE',

  // Module Registry
  MODULE_REGISTRY_VIEW: 'MODULE_REGISTRY_VIEW',
  MODULE_REGISTRY_MANAGE: 'MODULE_REGISTRY_MANAGE',

  // Roles
  ROLES_VIEW: 'ROLES_VIEW',
  ROLES_CREATE: 'ROLES_CREATE',
  ROLES_UPDATE: 'ROLES_UPDATE',
  ROLES_DELETE: 'ROLES_DELETE',

  // Users
  USERS_VIEW: 'USERS_VIEW',
  USERS_CREATE: 'USERS_CREATE',
  USERS_UPDATE: 'USERS_UPDATE',
  USERS_DELETE: 'USERS_DELETE',

  // Permissions
  PERMISSIONS_VIEW: 'PERMISSIONS_VIEW',

  // Tenant Profile
  TENANT_PROFILE_VIEW: 'TENANT_PROFILE_VIEW',

  // Organizations (V26 — drives tenant org branding/logo)
  ORGANIZATIONS_VIEW: 'ORGANIZATIONS_VIEW',
  ORGANIZATIONS_UPDATE: 'ORGANIZATIONS_UPDATE',

  // Contract Templates
  CONTRACT_TEMPLATES_VIEW: 'CONTRACT_TEMPLATES_VIEW',
  CONTRACT_TEMPLATES_CREATE: 'CONTRACT_TEMPLATES_CREATE',
  CONTRACT_TEMPLATES_UPDATE: 'CONTRACT_TEMPLATES_UPDATE',
  CONTRACT_TEMPLATES_DELETE: 'CONTRACT_TEMPLATES_DELETE',
  CONTRACT_TEMPLATES_SUBMIT: 'CONTRACT_TEMPLATES_SUBMIT',
  CONTRACT_TEMPLATES_APPROVE: 'CONTRACT_TEMPLATES_APPROVE',
  CONTRACT_TEMPLATES_DEACTIVATE: 'CONTRACT_TEMPLATES_DEACTIVATE',

  // Envelopes (signing requests)
  // MODULE_ENVELOPES (visibility=ALL) → read
  // MODULE_ENVELOPE_FLOW (visibility=WORKPAY) → write
  ENVELOPES_VIEW: 'ENVELOPES_VIEW',
  ENVELOPES_CREATE: 'ENVELOPES_CREATE',
  ENVELOPES_UPDATE: 'ENVELOPES_UPDATE',
  ENVELOPES_SEND: 'ENVELOPES_SEND',
  ENVELOPES_VOID: 'ENVELOPES_VOID',
  ENVELOPES_RESEND: 'ENVELOPES_RESEND',
  ENVELOPES_REVISE: 'ENVELOPES_REVISE',
  ENVELOPES_DOWNLOAD: 'ENVELOPES_DOWNLOAD',

  // Document Types
  DOCUMENT_TYPES_VIEW: 'DOCUMENT_TYPES_VIEW',
  DOCUMENT_TYPES_CREATE: 'DOCUMENT_TYPES_CREATE',
  DOCUMENT_TYPES_UPDATE: 'DOCUMENT_TYPES_UPDATE',
  DOCUMENT_TYPES_DELETE: 'DOCUMENT_TYPES_DELETE',

  // Document Snapshots (tenant-side contract review)
  DOCUMENT_SNAPSHOTS_VIEW: 'DOCUMENT_SNAPSHOTS_VIEW',
  DOCUMENT_SNAPSHOTS_UPDATE: 'DOCUMENT_SNAPSHOTS_UPDATE',

  // Document Requests — MODULE_ALL_DOCUMENTS (document-vault surface).
  // Backend mongo seed: backend/platform-service/.../V3__module_employee_documents_group.js
  // Note: no DOCUMENT_REQUESTS_UPDATE in the seed; rename is treated as CREATE-class.
  DOCUMENT_REQUESTS_VIEW: 'DOCUMENT_REQUESTS_VIEW',
  DOCUMENT_REQUESTS_CREATE: 'DOCUMENT_REQUESTS_CREATE',
  DOCUMENT_REQUESTS_DELETE: 'DOCUMENT_REQUESTS_DELETE',

  // Audit — watcher-service (gateway-authenticated). See spec
  // docs/superpowers/specs/2026-04-27-audit-trail-design.md for context.
  AUDIT_READ: 'AUDIT_READ',

} as const;

export type PermissionCode = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

// ============================================
// Route → Permission Map
// ============================================

/**
 * Maps route path prefixes to required permission codes.
 * Multiple codes per route = OR logic (user needs ANY ONE).
 * Order matters — more specific prefixes should come first.
 * A route with no entry in this map is accessible to all authenticated users.
 */
const WORKPAY_ROUTE_PERMISSIONS: [prefix: string, permissions: PermissionCode[]][] = [
  // More specific /add routes first — require create or update permission
  ['/w/form/add', [PERMISSIONS.FORMS_CREATE, PERMISSIONS.FORMS_UPDATE]],
  ['/w/roles/add', [PERMISSIONS.ROLES_CREATE, PERMISSIONS.ROLES_UPDATE]],
  ['/w/entity-templates/add', [PERMISSIONS.ENTITY_TEMPLATES_CREATE, PERMISSIONS.ENTITY_TEMPLATES_UPDATE]],
  ['/w/employee-templates/add', [PERMISSIONS.EMPLOYEE_TEMPLATES_CREATE, PERMISSIONS.EMPLOYEE_TEMPLATES_UPDATE]],
  // General view routes
  ['/w/employment-types', [PERMISSIONS.EMPLOYMENT_TYPES_VIEW]],
  ['/w/form', [PERMISSIONS.FORMS_VIEW]],
  ['/w/tenant-templates', [PERMISSIONS.TENANT_TEMPLATES_VIEW]],
  ['/w/entity-templates', [PERMISSIONS.ENTITY_TEMPLATES_VIEW]],
  ['/w/employee-templates', [PERMISSIONS.EMPLOYEE_TEMPLATES_VIEW]],
  ['/w/product-types', [PERMISSIONS.PRODUCT_TYPES_VIEW]],
  ['/w/modules', [PERMISSIONS.MODULE_REGISTRY_VIEW]],
  ['/w/dashboard', [PERMISSIONS.TENANTS_VIEW]],
  ['/w/entities', [PERMISSIONS.ENTITIES_VIEW]],
  ['/w/employees', [PERMISSIONS.EMPLOYEES_VIEW]],
  ['/w/roles', [PERMISSIONS.ROLES_VIEW, PERMISSIONS.ROLES_CREATE]],
  ['/w/users', [PERMISSIONS.USERS_VIEW, PERMISSIONS.USERS_CREATE]],
  // Document Types
  ['/w/document-types', [PERMISSIONS.DOCUMENT_TYPES_VIEW]],
  // Document Signing
  ['/w/signing-requests/new', [PERMISSIONS.ENVELOPES_CREATE]],
  ['/w/document-templates', [PERMISSIONS.CONTRACT_TEMPLATES_VIEW]],
  // Signing request edit route — more specific than parent /w/signing-requests; list before parent
  ['/w/signing-requests', [PERMISSIONS.ENVELOPES_VIEW]],
  // Cross-org tenant snapshots (workpay admin)
  ['/w/contracts', [PERMISSIONS.CONTRACT_TEMPLATES_VIEW]],
  // Document Vault surface — gated on DOCUMENT_REQUESTS_VIEW per MODULE_ALL_DOCUMENTS
  // seed (V3__module_employee_documents_group.js).
  ['/w/documents', [PERMISSIONS.DOCUMENT_REQUESTS_VIEW]],
];

const TENANT_ROUTE_PERMISSIONS: [prefix: string, permissions: PermissionCode[]][] = [
  ['/t/roles/add', [PERMISSIONS.ROLES_CREATE, PERMISSIONS.ROLES_UPDATE]],
  ['/t/profile', [PERMISSIONS.TENANT_PROFILE_VIEW]],
  ['/t/employees', [PERMISSIONS.EMPLOYEES_VIEW]],
  ['/t/roles', [PERMISSIONS.ROLES_VIEW, PERMISSIONS.ROLES_CREATE]],
  ['/t/users', [PERMISSIONS.USERS_VIEW, PERMISSIONS.USERS_CREATE]],
  // Contract Templates (read-only for tenant persona)
  ['/t/document-templates', [PERMISSIONS.CONTRACT_TEMPLATES_VIEW]],
  // Document Snapshots (tenant contract review)
  ['/t/contracts', [PERMISSIONS.DOCUMENT_SNAPSHOTS_VIEW]],
  // Document Vault surface (tenant org-scoped) — same permission as /w/documents.
  ['/t/documents', [PERMISSIONS.DOCUMENT_REQUESTS_VIEW]],
  // Signing Requests — tenants get read-only via MODULE_ENVELOPES (visibility=ALL).
  ['/t/signing-requests', [PERMISSIONS.ENVELOPES_VIEW]],
  // Departments + Job Titles (V30 — tenant-scoped configuration)
  ['/t/departments', [PERMISSIONS.DEPARTMENTS_VIEW]],
  ['/t/job-titles', [PERMISSIONS.JOB_TITLES_VIEW]],
];

// ============================================
// Helpers
// ============================================

/**
 * Find the required permissions for a given pathname.
 * Returns undefined if the route has no permission requirement.
 */
export function getRequiredPermissions(
  pathname: string,
): PermissionCode[] | undefined {
  const map = pathname.startsWith('/w')
    ? WORKPAY_ROUTE_PERMISSIONS
    : pathname.startsWith('/t')
      ? TENANT_ROUTE_PERMISSIONS
      : [];

  for (const [prefix, permissions] of map) {
    if (pathname.startsWith(prefix)) {
      return permissions;
    }
  }
  return undefined;
}

/**
 * Check if a user can access a route based on their permissions.
 * Routes with no permission requirement are always accessible.
 * Multiple permissions per route use OR logic — user needs at least one.
 */
export function canAccessRoute(
  pathname: string,
  userPermissions: string[],
): boolean {
  const required = getRequiredPermissions(pathname);
  if (!required) return true;
  return required.some((code) => userPermissions.includes(code));
}

/**
 * Get the first accessible route for a given org context.
 * Iterates through sidebar items in display order and returns the first
 * route the user has permission for.
 *
 * Returns null if no accessible route is found (should not happen in practice).
 */
export function getFirstAccessibleRoute(
  orgPrefix: '/w' | '/t',
  userPermissions: string[],
): string | null {
  const orderedRoutes =
    orgPrefix === '/w'
      ? WORKPAY_ROUTE_PERMISSIONS
      : TENANT_ROUTE_PERMISSIONS;

  for (const [prefix, permissions] of orderedRoutes) {
    if (permissions.some((code) => userPermissions.includes(code))) {
      return prefix;
    }
  }
  return null;
}
